import { FunctionComponent, PropsWithChildren, useState } from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { createQueryClient, ICreateQueryClientOptions } from './query-client';

export interface IReactQueryProviderProps extends ICreateQueryClientOptions {
  /**
   * Whether to show React Query DevTools.
   * Defaults to true.
   *
   * Note: DevTools are automatically excluded from production builds
   * via tree-shaking when process.env.NODE_ENV === 'production'.
   * This flag is useful for explicitly disabling DevTools in development
   * if needed.
   */
  showDevTools?: boolean;
}

/**
 * React Query provider component that wraps the application
 * with QueryClientProvider and optional DevTools.
 *
 * @example
 * ```tsx
 * <ReactQueryProvider reporting={reporting}>
 *   <App />
 * </ReactQueryProvider>
 * ```
 */
export const ReactQueryProvider: FunctionComponent<
  PropsWithChildren<IReactQueryProviderProps>
> = ({ children, showDevTools = true, ...queryClientOptions }) => {
  // Create QueryClient once and keep it stable across re-renders
  const [queryClient] = useState(() => createQueryClient(queryClientOptions));

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {showDevTools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

ReactQueryProvider.displayName = 'ReactQueryProvider';
